import { DashboardTaskWithEntityReferences, TasksResponse } from "@dune/types";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Tabs,
	TabsList,
	TabsContent,
	TabsTriggerWithNumber,
} from "@dune/ui";
import {
	getEntityNavigationUrlForTasks,
	formatEntityReference,
} from "@dune/utils";
import { Hourglass01 } from "@untitled-ui/icons-react";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTaskActions } from "../../../stores/task.store";
import { useMe } from "../../../api/queries/users.queries";
import { RoleEnum } from "../../../../../../packages/vars/src";

const getStatusColor = (status: string) => {
	switch (status) {
		case "TO_DO":
			return "bg-orange-badge-background text-orange-badge-text border border-orange-badge-border rounded-full";
		case "IN_PROGRESS":
			return "bg-blue-badge-background text-blue-badge-text border border-blue-badge-border rounded-full";
		default:
			return "bg-gray-badge-background text-gray-badge-text border border-gray-badge-border rounded-full";
	}
};

const getStatusLabel = (status: string) => {
	switch (status) {
		case "TO_DO":
			return "À faire";
		case "IN_PROGRESS":
			return "En cours";
		default:
			return status;
	}
};

const formatDate = (date: Date | string | null) => {
	if (!date) return "Pas de date";
	const dateObj = typeof date === "string" ? new Date(date) : date;
	return dateObj.toLocaleDateString("fr-FR");
};

const formatEntityType = (entityType: string) => {
	switch (entityType) {
		case "BROKER":
			return "Apporteur";
		case "CONSTRUCTION_SITE":
			return "Chantier";
		case "PROPOSITION":
			return "Proposition";
		default:
			return entityType;
	}
};

interface TasksSectionProps {
	tasksData?: TasksResponse;
	isLoading: boolean;
	error: Error | null;
}

type TabType = "my-tasks" | "underwriters" | "admin";

const TaskList: React.FC<{
	tasks: DashboardTaskWithEntityReferences[];
	handleTaskClick: (task: DashboardTaskWithEntityReferences) => void;
}> = ({ tasks, handleTaskClick }) => {
	if (tasks.length === 0) {
		return (
			<div className="flex items-center justify-center h-full">
				<div className="text-gray-500">Aucune tâche disponible</div>
			</div>
		);
	}

	return (
		<div className="h-full overflow-y-auto space-y-3 pr-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
			{tasks.map((task) => (
				<Card
					key={task.id}
					className={`p-4 bg-white border border-gray-100 transition-all duration-200 ${
						task.relatedEntities && task.relatedEntities.length > 0
							? "cursor-pointer hover:border-blue-300  hover:bg-blue-50"
							: ""
					}`}
					noShadow
					onClick={() => {
						if (task.relatedEntities && task.relatedEntities.length > 0) {
							handleTaskClick(task);
						}
					}}
				>
					<div className="flex items-start justify-between">
						<div className="flex-1 min-w-0">
							<div className="flex items-center gap-2 mb-2">
								<span
									className={`text-xs font-medium px-2 py-1 rounded ${getStatusColor(task.status)}`}
								>
									{getStatusLabel(task.status)}
								</span>
							</div>
							<p className="font-medium text-sm text-gray-900 mb-1">
								{formatEntityType(task.relatedEntities[0].entityType)}{" "}
								{formatEntityReference(
									task.relatedEntities[0].entityType,
									task.relatedEntities[0].entityReference,
								)}
							</p>
							{task.description && (
								<p className="text-sm text-gray-600 mb-2">{task.description}</p>
							)}
							<div className="mt-2 text-xs text-gray-500 pb-2">
								{task.title}
							</div>
							<div className="flex items-center gap-1 text-xs text-gray-500">
								<Hourglass01 className="h-4 w-4" />
								<span>{formatDate(task.dueDate)}</span>
							</div>
						</div>
					</div>
				</Card>
			))}
		</div>
	);
};

export const TasksSection: React.FC<TasksSectionProps> = ({
	tasksData,
	isLoading,
	error,
}) => {
	const navigate = useNavigate();
	const { selectTask } = useTaskActions();
	const { me: user } = useMe();
	const [activeTab, setActiveTab] = useState<TabType>("my-tasks");

	const tasks =
		tasksData?.tasks.filter((task) => {
			if (task.status === "DONE") {
				return false;
			}
			return (
				task.dashboardTaskVisibility.some((visibility) => {
					if (user?.subrole && visibility.subrole) {
						if (
							visibility.subrole === "DUNE_UNDERWRITER" &&
							user.subrole === "DUNE_ADMIN"
						)
							return true;
						return visibility.subrole === user.subrole;
					}
					return visibility.role === user?.role;
				}) ?? false
			);
		}) ?? [];

	const showTabs = user?.role === RoleEnum.DUNE;

	const classifyTasks = useMemo(() => {
		const myTasks: DashboardTaskWithEntityReferences[] = [];
		const underwriterTasks: DashboardTaskWithEntityReferences[] = [];
		const adminTasks: DashboardTaskWithEntityReferences[] = [];

		for (const task of tasks) {
			if (task.assigneeId === user?.id) {
				myTasks.push(task);
			} else if (task.assignmentSubRole === "DUNE_UNDERWRITER") {
				underwriterTasks.push(task);
			} else if (task.assignmentSubRole === "DUNE_ADMIN") {
				adminTasks.push(task);
			} else if (task.assignmentRole === "DUNE") {
				underwriterTasks.push(task);
			}
		}

		return { myTasks, underwriterTasks, adminTasks };
	}, [tasks, user?.id]);

	if (error) {
		return (
			<Card className="flex-1 flex flex-col overflow-hidden h-full bg-gray-50 rounded-lg border border-gray-200">
				<CardHeader className="flex-shrink-0 pb-4">
					<CardTitle className="text-xl font-semibold text-red-600">
						Erreur lors du chargement des tâches
					</CardTitle>
				</CardHeader>
			</Card>
		);
	}

	const handleTaskClick = (task: DashboardTaskWithEntityReferences) => {
		if (task.relatedEntities && task.relatedEntities.length > 0) {
			const entity = task.relatedEntities[0];
			selectTask(task.id.toString());
			const url = getEntityNavigationUrlForTasks(
				entity.entityType,
				entity.entityId.toString(),
			);
			navigate(url);
		}
	};

	const getAvailableTabs: () => TabType[] = () => {
		if (!showTabs) return [];
		if (user?.subrole === "DUNE_ADMIN") {
			return ["my-tasks", "admin", "underwriters"];
		}
		if (user?.subrole === "DUNE_UNDERWRITER") {
			return ["my-tasks", "underwriters"];
		}
		return [];
	};

	const availableTabs = getAvailableTabs();

	if (!showTabs) {
		return (
			<Card className="flex-1 flex flex-col overflow-hidden h-full bg-gray-50 rounded-lg border border-gray-100">
				<CardContent className="flex-1 overflow-hidden p-4 pt-0 bg-transparent">
					<div className="flex-shrink-0 pb-4">
						<div className="flex items-center gap-2 rounded-lg px-3 py-2">
							<div className="flex items-center text-xl font-semibold">
								Tâches
								{!isLoading && (
									<span className="ml-2 text-sm font-normal text-gray-500 rounded-full px-2 py-1">
										{tasks.length}
									</span>
								)}
							</div>
						</div>
					</div>
					<div className="h-full flex flex-col">
						<div className="flex-1 overflow-hidden">
							{isLoading ? (
								<div className="flex items-center justify-center h-full">
									<div className="text-gray-500">Chargement des tâches...</div>
								</div>
							) : (
								<TaskList tasks={tasks} handleTaskClick={handleTaskClick} />
							)}
						</div>
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className="flex-1 flex flex-col overflow-hidden h-full bg-gray-50 rounded-lg border border-gray-100">
			<CardContent className="flex-1 overflow-hidden p-4 pt-0 bg-transparent">
				<div className="flex-shrink-0 pb-4">
					<div className="flex items-center text-xl font-semibold">Tâches</div>
				</div>
				<div className="h-full flex flex-col">
					{isLoading ? (
						<div className="flex items-center justify-center h-full">
							<div className="text-gray-500">Chargement des tâches...</div>
						</div>
					) : (
						<Tabs
							value={activeTab}
							onValueChange={(value) => setActiveTab(value as TabType)}
							className="h-full flex flex-col"
						>
							<TabsList className="w-fit">
								{availableTabs.includes("my-tasks") && (
									<TabsTriggerWithNumber
										value="my-tasks"
										number={classifyTasks.myTasks.length}
									>
										Mes tâches
									</TabsTriggerWithNumber>
								)}
								{availableTabs.includes("admin") && (
									<TabsTriggerWithNumber
										value="admin"
										number={classifyTasks.adminTasks.length}
									>
										Admin
									</TabsTriggerWithNumber>
								)}
								{availableTabs.includes("underwriters") && (
									<TabsTriggerWithNumber
										value="underwriters"
										number={classifyTasks.underwriterTasks.length}
									>
										Souscripteurs
									</TabsTriggerWithNumber>
								)}
							</TabsList>
							<TabsContent value="my-tasks" className="flex-1 overflow-hidden">
								<TaskList
									tasks={classifyTasks.myTasks}
									handleTaskClick={handleTaskClick}
								/>
							</TabsContent>
							<TabsContent
								value="underwriters"
								className="flex-1 overflow-hidden"
							>
								<TaskList
									tasks={classifyTasks.underwriterTasks}
									handleTaskClick={handleTaskClick}
								/>
							</TabsContent>
							<TabsContent value="admin" className="flex-1 overflow-hidden">
								<TaskList
									tasks={classifyTasks.adminTasks}
									handleTaskClick={handleTaskClick}
								/>
							</TabsContent>
						</Tabs>
					)}
				</div>
			</CardContent>
		</Card>
	);
};
